import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
// libs
import NextImage from "next/image";
import axios from "src/libs/axios";
import debounce from "lodash/debounce";
// components
import ClinicCardWeb from "src/components/ui/Cards/ClinicWeb";
import ClinicCardMobile from "src/components/ui/Cards/ClinicMobile";
import Pagination from "src/components/ui/Pagination";
import Link from "src/components/ui/Link";
import Button from "src/components/ui/button";
import Mapbox from "src/components/ui/Map";
import { ModalMap } from "src/components/ui/Modals";
// hooks
import Router, { useRouter } from "next/router";
import { useModal, useWindowSize, useAppDispatch, useAppSelector } from "src/hooks";
import useSWR from "swr";
// const
import { EVariants } from "src/constants/variants";
import { MAP_MODAL, SEARCH_FILTERS_MODAL } from "src/constants/modals";
import { ENDPOINTS } from "src/constants/endpoints";
import { sizesMedia } from "src/constants/media";
import { SEARCH_AS_I_MOVE } from "src/constants/variables";
// redux
import { setPathClinics, authSelector, permissionSelector } from "src/redux/slices";
import { variablesStateSelector } from "src/redux/slices/variables/selectors";
import { VariablesState } from "src/redux/slices/variables";
// utils
import numberClinicsPage from "src/utils/pagination";
import { getCountry } from "src/utils/countries";
import { getLocationByCenter } from "src/utils/map";
// theme
import {
  ClinicCardMobileWrapper,
  ClinicCardWebWrapper,
  GridWrapper,
  ISearchProps,
  MapWrapper,
  Mobile,
  MobileViewMapButton,
  MobileViewMapButtonText,
  Web,
  FiltersWrapper,
  NumberFilterContainer,
  TextPagination,
  TopServicesContainer,
  BadgeContainer,
} from "src/components/containers/search";
// types
import { TClinicDetailsSearch, IService } from "src/types";

import * as permissions from "../../../constants/permissions";
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";
import { Body, LargeTitle } from "src/theme/typography";

const TOP_SERVICES_COUNT = 3;
const DEFAULT_TEXT = "Vets in search area";
const REFETCH_DELAY = 1000;

const SearchContainer: React.FC<ISearchProps> = ({ clinics, count, mapProps, searchArea }) => {
  const size = useWindowSize();
  const dispatch = useAppDispatch();
  const { openModal } = useModal();
  const { query, asPath } = useRouter();
  const [searchTitle, setSearchTitle] = useState<string>("");
  const user = useAppSelector(authSelector);
  const isSearchOnMoveTriggered = useRef(false);

  const VariablesStateSelected: VariablesState = useAppSelector(variablesStateSelector);
  const searchOnMove = VariablesStateSelected[SEARCH_AS_I_MOVE];

  const [currentNumberPage, setCurrentNumberPage] = useState(1);
  // const initialService = query.area?.[0] === 'service' ? { [query?.area?.[1]]: true } : {}
  const [topServices, setTopServices] = useState<{ [key: string]: boolean }>({});

  const geolocationPermission = useAppSelector(permissionSelector(permissions.GEOLOCATION));
  const isGeolocationDenied = geolocationPermission === "denied";

  const setTitleByQuery = useCallback(
    debounce(async (locationQuery) => {
      const center = (locationQuery?.geo_order as string).split(",");
      try {
        const location = await getLocationByCenter(center);
        const title = location ? `Vets in ${location}` : DEFAULT_TEXT;
        setSearchTitle(title);
      } catch (err) {
        console.error(err);
      }
    }, REFETCH_DELAY),
    [],
  );

  const formatQueryParam = (param: string | undefined) => param?.replace("-", " ");

  useEffect(() => {
    const isMultipleServicesAndAllowedGeolocation =
      !isGeolocationDenied && user.location && Array.isArray(query.services);
    const isServiceUrlEnity = query.area?.includes("service");
    const isSingleService = query.services && typeof query.services === "string";
    const isQuerySearch = query.area?.includes("search");
    if (searchOnMove) {
      isSearchOnMoveTriggered.current = true;
    }
    if (isServiceUrlEnity) {
      const serviceName = formatQueryParam(query.area?.[1]);
      setSearchTitle(`Vets providing ${serviceName}`);
      return;
    }
    if (isSingleService) {
      setSearchTitle(`Vets providing ${formatQueryParam(query.services as string)}`);
      return;
    }
    if (isMultipleServicesAndAllowedGeolocation && !searchOnMove) {
      if (isSearchOnMoveTriggered.current) {
        setTitleByQuery(query);
      } else {
        setSearchTitle(`Vets in ${user.location}`);
      }
      return;
    }
    if (isQuerySearch) {
      setTitleByQuery(query);
      return;
    }
    if (searchArea) {
      setSearchTitle(`Vets in ${searchArea}`);
      return;
    }
    if (user.defaultCity && !user.location && !isSearchOnMoveTriggered.current) {
      setSearchTitle(`Vets in ${user.defaultCity}`);
      return;
    }
    setSearchTitle(DEFAULT_TEXT);
  }, [searchArea, query, user, searchOnMove, query?.geo_order]);

  const country = getCountry(Router.router?.locale, "locale")?.name;
  const servicesResponse = useSWR(
    `${ENDPOINTS.GET.services}?_sort=clinics:desc&_limit=${TOP_SERVICES_COUNT}&clinics.country=${country}`,
    axios,
  );

  const servicesToRender = (servicesResponse.data?.data || [])
    .map((service: IService) => ({ id: service.id, name: service.name }))
    .filter((service: { id: IService["id"]; name: IService["name"] }) => service.id && service.name)
    .slice(0, TOP_SERVICES_COUNT);

  const numberFilters = !query.hasOwnProperty("services") ? null : Object.keys(topServices)?.length;
  const isFiltersApplied = !!numberFilters || query.hasOwnProperty("species_items");

  const handleServiceClick = (name: string) => () =>
    setTopServices((prevServices) => ({ ...prevServices, [name]: !prevServices[name] }));

  const params = { ...Router.router?.query };
  const selectedTopServices = useMemo(
    () => Object.keys(topServices).filter((s) => !!topServices[s]),
    [topServices],
  );

  useDeepCompareEffect(() => {
    if (Router.query.area?.includes("service") && Object.keys(selectedTopServices).length === 1)
      return;
    params.services = selectedTopServices;
    params._start = "0";
    if (Router.query.area?.includes("service" || "search") || Router.query.geo_order) {
      params.area = ["search"];
    }

    if (Object.keys(topServices).length !== 0) {
      Router.push(
        {
          pathname: Router.router?.pathname,
          query: params,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [selectedTopServices]);

  useDeepCompareEffect(() => {
    const queryService = query.area?.[0] === "service" ? { [query?.area?.[1]]: true } : undefined;
    const newServices =
      typeof params.services === "string"
        ? { [params.services]: true }
        : (params.services || []).reduce((acc, curr) => ({ ...acc, [curr]: true }), {});
    setTopServices(queryService || newServices);
  }, [params]);

  const openMapModal = () => openModal(MAP_MODAL);
  const openSearchFiltersModal = () => openModal(SEARCH_FILTERS_MODAL);

  useEffect(() => {
    dispatch(setPathClinics(asPath));
  }, [asPath]);

  const trackClinicClick = () =>
    amplitudeEvent(formatEventName("search_results", "results", "clinic"));

  return (
    <GridWrapper>
      <div>
        <Body>{count} clinics</Body>
        <LargeTitle>{searchTitle}</LargeTitle>

        <FiltersWrapper>
          {size.width > sizesMedia.laptop && (
            <TopServicesContainer>
              {servicesToRender.map(
                ({ id, name }: { id: IService["id"]; name: IService["name"] }) => (
                  <Button
                    key={id}
                    variant={EVariants.SECONDARY}
                    isToggle
                    isActive={topServices[name]}
                    style={{ marginRight: 8 }}
                    onClick={handleServiceClick(name)}
                  >
                    {name}
                  </Button>
                ),
              )}
            </TopServicesContainer>
          )}
          <BadgeContainer>
            <Button
              variant={EVariants.SECONDARY}
              onClick={() => {
                openSearchFiltersModal();
                amplitudeEvent(formatEventName("search_results", "filters", "edit_filters"));
              }}
              style={{ width: "100%" }}
            >
              {size.width < sizesMedia.tablet ? "Filters" : "More filters"}
            </Button>
            {numberFilters && <NumberFilterContainer>{numberFilters}</NumberFilterContainer>}
          </BadgeContainer>
        </FiltersWrapper>

        <Web>
          {clinics.map((item: TClinicDetailsSearch) => (
            <ClinicCardWebWrapper key={item.id} onClick={trackClinicClick}>
              <Link
                href={item.slug.startsWith("/") ? `/clinics${item.slug}` : `/clinics/${item.slug}`}
              >
                <ClinicCardWeb
                  name={item.name}
                  logo_url={item.logo_url}
                  address={item.address}
                  rating={item.rating || []}
                  services={item.services}
                  fromPetsapp={item.from_petsapp}
                />
              </Link>
            </ClinicCardWebWrapper>
          ))}
        </Web>
        <Mobile>
          {clinics.map((item: TClinicDetailsSearch) => (
            <ClinicCardMobileWrapper key={item.id} onClick={trackClinicClick}>
              <Link
                href={item.slug.startsWith("/") ? `/clinics${item.slug}` : `/clinics/${item.slug}`}
              >
                <ClinicCardMobile
                  name={item.name}
                  logo_url={item.logo_url}
                  address={item.address}
                  rating={item.rating || []}
                  services={item.services}
                  fromPetsapp={item.from_petsapp}
                />
              </Link>
            </ClinicCardMobileWrapper>
          ))}
        </Mobile>

        <Pagination
          totalCount={count}
          shallow={false}
          setCurrentNumberPage={setCurrentNumberPage}
        />

        <TextPagination>
          {numberClinicsPage(currentNumberPage, +count, isFiltersApplied)}
        </TextPagination>
      </div>

      <MapWrapper>
        {!!mapProps.containerProps.ne && !!mapProps.containerProps.sw && (
          <Mapbox containerProps={mapProps.containerProps} />
        )}
      </MapWrapper>

      {!!mapProps.containerProps.ne && !!mapProps.containerProps.sw && (
        <>
          <MobileViewMapButton onClick={openMapModal}>
            <NextImage src="/map/map-white.svg" width={18} height={18} layout="fixed" />
            <MobileViewMapButtonText>View map</MobileViewMapButtonText>
          </MobileViewMapButton>

          <ModalMap {...mapProps} />
        </>
      )}
    </GridWrapper>
  );
};

export default SearchContainer;
